import { RefObject, useCallback, useId, useLayoutEffect, useState } from "react";

import { Clock, EmptystateSearchNoResultsColored, Trash } from "components/icons/generated";
import { SEARCH_PLACEHOLDERS } from "constants/searchPlaceholders";
import Box from "ds/components/Box";
import DropdownWrapper from "ds/components/Dropdown/DropdownWrapper";
import DropdownHeaderSearch from "ds/components/Dropdown/Header/Search";
import TextEllipsis from "ds/components/TextEllipsis";
import Typography from "ds/components/Typography";
import useAssistantPanelOverlayDismiss from "components/AssistantPanel/hooks/useOverlayDismiss";
import ButtonIcon from "ds/components/ButtonIcon";
import DropdownSection from "ds/components/Dropdown/Section";
import DropdownSectionItem from "ds/components/Dropdown/SectionItem";
import DropdownListWrapper from "ds/components/Dropdown/List/New/Wrapper";
import EmptyState from "ds/components/EmptyState";

import { AiChatSession } from "../../types";
import useAiChatHistory from "../useHistory";
import styles from "./styles.module.css";

type AiChatHistoryDropdownProps = {
  deleteChat: (chatId: string) => void;
  chats: AiChatSession[];
  currentChatId: string;
  loading: boolean;
  onSelectChat: (chatId: string) => void;
  isOpen: boolean;
  onToggle: () => void;
  showOverlay: boolean;
  boundaryRef?: RefObject<HTMLElement | null>;
};

const AiChatHistoryDropdown = ({
  deleteChat,
  chats,
  currentChatId,
  loading,
  onSelectChat,
  isOpen,
  onToggle,
  showOverlay,
  boundaryRef,
}: AiChatHistoryDropdownProps) => {
  const dropdownId = useId();
  const listboxId = useId();
  const [maxWidth, setMaxWidth] = useState<number | undefined>();

  const { searchInput, setSearchInput, sections, noSearchResults } = useAiChatHistory(chats);

  const closeDropdown = useCallback(() => {
    if (isOpen) {
      onToggle();
    }
  }, [isOpen, onToggle]);

  const { wrapperRef } = useAssistantPanelOverlayDismiss({
    isOpen: showOverlay,
    onClose: closeDropdown,
    swallowTriggerPress: false,
  });

  useLayoutEffect(() => {
    if (!showOverlay) return;
    const wrapper = wrapperRef.current;
    const boundary = boundaryRef?.current;
    if (!wrapper || !boundary) return;

    const update = () => {
      const wrapperRect = wrapper.getBoundingClientRect();
      const boundaryRect = boundary.getBoundingClientRect();
      const boundaryPaddingRight = parseFloat(getComputedStyle(boundary).paddingRight) || 0;
      const innerRight = boundaryRect.right - boundaryPaddingRight;
      setMaxWidth(Math.max(0, innerRight - wrapperRect.left));
    };

    update();

    const observer = new ResizeObserver(update);
    observer.observe(boundary);
    return () => observer.disconnect();
  }, [showOverlay, boundaryRef, wrapperRef]);

  return (
    <DropdownWrapper ref={wrapperRef}>
      <ButtonIcon
        icon={Clock}
        variant="ghost"
        size="small"
        onPress={onToggle}
        active={isOpen}
        aria-controls={showOverlay ? dropdownId : undefined}
        aria-expanded={isOpen}
      >
        History
      </ButtonIcon>

      {showOverlay && (
        <DropdownListWrapper id={dropdownId} className={styles.dropdownList} style={{ maxWidth }}>
          <DropdownHeaderSearch
            listboxId={listboxId}
            ariaLabel="AI chat history"
            searchPlaceholder={SEARCH_PLACEHOLDERS.AI_CHAT_HISTORY}
            searchValue={searchInput}
            setSearchValue={setSearchInput}
            searchLoading={loading}
          />

          {noSearchResults && (
            <EmptyState
              title="No search results"
              icon={EmptystateSearchNoResultsColored}
              fullHeight
            />
          )}
          <Box id={listboxId} role="menu" aria-label="Chat history" direction="column" scrollable>
            {sections.map((section) => (
              <DropdownSection
                topDivider={false}
                title={section.label}
                key={section.label}
                className={styles.dropdownSection}
              >
                {section.chats.map((chat) => (
                  <Box relative key={chat.id} className={styles.chatRow}>
                    <DropdownSectionItem
                      onClick={() => {
                        onSelectChat(chat.id);
                        closeDropdown();
                      }}
                      active={chat.id === currentChatId}
                      className={styles.dropdownSectionItem}
                    >
                      <Box direction="row" align="center" gap="large" fullWidth justify="between">
                        <TextEllipsis tooltip={chat.name}>
                          {(props) => (
                            <Typography {...props} variant="p-body3" color="primary" tag="span">
                              {chat.name}
                            </Typography>
                          )}
                        </TextEllipsis>
                      </Box>
                    </DropdownSectionItem>
                    <ButtonIcon
                      className={styles.deleteButton}
                      icon={Trash}
                      variant="ghostDanger"
                      size="small"
                      onPress={() => deleteChat(chat.id)}
                    >
                      Delete
                    </ButtonIcon>
                  </Box>
                ))}
              </DropdownSection>
            ))}
          </Box>
        </DropdownListWrapper>
      )}
    </DropdownWrapper>
  );
};

export default AiChatHistoryDropdown;
